import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
/* eslint-disable no-void */
import { useEffect, useState } from 'react';
import { getCookie, setCookie } from '@beef/utils';
import { usePrevious } from '@beef/react';
import { useMutableRef } from '@carnica/utils/hooks';
import { ymPushBlockCaptcha, ymPushViewMesCaptchaDone, ymPushViewMesErrorCaptcha, ymPushViewMesRetryCaptcha } from "../../../../../shared/analytics";
import { FSMStages, useAppDispatch, useAppSelector, useFSM } from "../../../../../shared/model";
import { CAPTCHA_ACCESS_COOKIE_NAME, CAPTCHA_ACTIVE_MESSAGE_ID, CAPTCHA_BLOCKED_MESSAGE_KEY, CAPTCHA_MAX_RETRIES, CAPTCHA_STAGE_KEY, COOKIE_TOKEN_INVALID_MESSAGE, MAX_RECONNECT_ATTEMPTS } from "../../../../../shared/config";
import { selectCaptchaRetryCount, setActiveCaptchaMessageId, setCaptchaAccessToken, setCaptchaData, setRecreateCount, setRetryCount } from "../../../model/redux";
import { ECaptchaStage } from "./types";
import { getCaptchaData, getStage } from "./helper";
export var useCaptcha = function useCaptcha(_ref) {
  var _config$authZoneConfi;
  var config = _ref.config,
    reconnectCount = _ref.reconnectCount,
    generateCaptchaMessage = _ref.generateCaptchaMessage,
    validateCaptcha = _ref.validateCaptcha,
    setIsNoNAuthCaptchaStage = _ref.setIsNoNAuthCaptchaStage;
  var _useFSM = useFSM(),
    changeStage = _useFSM.changeStage,
    stage = _useFSM.stage,
    addReachedHandler = _useFSM.addReachedHandler;
  var _useState = useState('init'),
    _useState2 = _slicedToArray(_useState, 2),
    captchaStage = _useState2[0],
    setCaptchaStage = _useState2[1];
  var prevFSMStage = usePrevious(stage);
  var dispatch = useAppDispatch();
  var _ref2 = (_config$authZoneConfi = config.authZoneConfig) !== null && _config$authZoneConfi !== void 0 ? _config$authZoneConfi : {},
    zoneType = _ref2.zoneType;
  var retryRef = useMutableRef(useAppSelector(selectCaptchaRetryCount));
  var handleChangeStage = function handleChangeStage(newStage) {
    setCaptchaStage(newStage);
    setCookie({
      name: CAPTCHA_STAGE_KEY,
      value: newStage
    });
    void generateCaptchaMessage(newStage);
    if ([ECaptchaStage.retry, ECaptchaStage.start, ECaptchaStage.recaptcha].includes(newStage)) {
      void generateCaptchaMessage('captcha');
    }
  };
  var sendCaptchaMessage = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(value) {
      var response, data, type, message;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return validateCaptcha(value);
          case 2:
            response = _context.sent;
            if (response) {
              _context.next = 6;
              break;
            }
            void generateCaptchaMessage('error');
            return _context.abrupt("return");
          case 6:
            data = response.data, type = response.type;
            if (!(type === 'success')) {
              _context.next = 13;
              break;
            }
            changeStage(FSMStages.CONNECTING);
            setIsNoNAuthCaptchaStage(false);
            handleChangeStage('end');
            ymPushViewMesCaptchaDone(zoneType);
            return _context.abrupt("return");
          case 13:
            message = data === null || data === void 0 ? void 0 : data.message;
            if (!(data.status === 401 && message === COOKIE_TOKEN_INVALID_MESSAGE)) {
              _context.next = 18;
              break;
            }
            void generateCaptchaMessage('error');
            void generateCaptchaMessage('captcha');
            return _context.abrupt("return");
          case 18:
            if (retryRef.current + 1 > CAPTCHA_MAX_RETRIES) {
              changeStage(FSMStages.BLOCKED);
              handleChangeStage('blocked');
              ymPushBlockCaptcha(zoneType);
            } else {
              handleChangeStage('retry');
              ymPushViewMesErrorCaptcha(zoneType);
            }
          case 19:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function sendCaptchaMessage(_x) {
      return _ref3.apply(this, arguments);
    };
  }();
  var onBlockReached = function onBlockReached() {
    dispatch(setRetryCount(0));
    void generateCaptchaMessage('captcha', CAPTCHA_BLOCKED_MESSAGE_KEY);
  };
  useEffect(function () {
    addReachedHandler('captcha', onBlockReached);
  }, []);
  var resetCounters = function resetCounters() {
    dispatch(setRetryCount(0));
    dispatch(setRecreateCount(0));
  };
  var restart = function restart() {
    resetCounters();
    handleChangeStage(ECaptchaStage.recaptcha);
    ymPushViewMesRetryCaptcha(zoneType);
  };
  useEffect(function () {
    var isReconnectCaptchaNZ = reconnectCount > MAX_RECONNECT_ATTEMPTS && prevFSMStage === FSMStages.CONNECTING;
    if ((prevFSMStage === FSMStages.LOAD_DATA || isReconnectCaptchaNZ) && stage === FSMStages.CAPTCHA_NEEDED) {
      restart();
      return;
    }

    // Для тех случаев, когда страница перезагрузилась, но капча уже пройдена и мы находимся на 1 из 3 этапов: общение / подключение / загрузка истории
    if (stage === FSMStages.ACTIVE || stage === FSMStages.LOAD_DATA || stage === FSMStages.CONNECTING) {
      setCaptchaStage('end');
    }
  }, [stage]);
  useEffect(function () {
    var cookieStage = getStage(stage);
    setCaptchaStage(cookieStage);
    dispatch(setCaptchaData(getCaptchaData()));
    if (cookieStage === 'init') {
      resetCounters();
    }
  }, []);
  useEffect(function () {
    dispatch(setCaptchaAccessToken(getCookie(CAPTCHA_ACCESS_COOKIE_NAME) || ''));
    dispatch(setActiveCaptchaMessageId(getCookie(CAPTCHA_ACTIVE_MESSAGE_ID) || ''));
  }, []);
  return {
    sendCaptchaMessage: sendCaptchaMessage,
    handleChangeStage: handleChangeStage,
    generateCaptchaMessage: generateCaptchaMessage,
    captchaStage: captchaStage
  };
};